import { useState, useMemo } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils/cn';
import { faqItems } from '@/features/help-center/data/faq-data';
import { FAQ_CATEGORIES } from '@/features/help-center/types/help-center.types';
import { DocsLayout } from '../components/docs-layout';

// Categorias adicionales para el FAQ publico
const EXTRA_CATEGORIES: { value: string; label: string }[] = [
  { value: 'ventas', label: 'Ventas' },
  { value: 'facturacion', label: 'Facturacion' },
  { value: 'seguridad', label: 'Seguridad' },
];

interface ExtendedFaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

const extraFaqItems: ExtendedFaqItem[] = [
  // Ventas
  {
    id: 'vta-1',
    question: 'Cuanto cuesta Prixia?',
    answer:
      'Ofrecemos 3 planes: Starter ($49/mes, hasta 5 usuarios, 100 cotizaciones/mes), Professional ($149/mes, hasta 25 usuarios, cotizaciones ilimitadas, IA incluida) y Enterprise (precio personalizado, usuarios ilimitados, SLA garantizado). Todos los planes incluyen una prueba gratuita de 30 dias.',
    category: 'ventas',
  },
  {
    id: 'vta-2',
    question: 'Hay descuentos por pago anual?',
    answer:
      'Si. Al contratar un plan anual recibes un descuento del 20% sobre el precio mensual. Es decir, el plan Starter queda en $39/mes y el Professional en $119/mes facturados anualmente.',
    category: 'ventas',
  },
  {
    id: 'vta-3',
    question: 'Puedo cambiar de plan en cualquier momento?',
    answer:
      'Si. Puedes actualizar o cambiar tu plan en cualquier momento desde la configuracion de tu cuenta. Si actualizas a un plan superior, se aplica un prorrateo por los dias restantes del ciclo de facturacion actual.',
    category: 'ventas',
  },
  {
    id: 'vta-4',
    question: 'Ofrecen descuentos para empresas con muchos usuarios?',
    answer:
      'Si. Para empresas con mas de 50 usuarios ofrecemos precios personalizados a traves del plan Enterprise. Contacta a nuestro equipo de ventas para una cotizacion a medida que incluya descuento por volumen.',
    category: 'ventas',
  },
  // Facturacion
  {
    id: 'fac-1',
    question: 'Que metodos de pago aceptan?',
    answer:
      'Aceptamos tarjetas de credito (Visa, Mastercard, American Express), transferencia bancaria y para planes Enterprise tambien facturacion directa con terminos de pago NET 30.',
    category: 'facturacion',
  },
  {
    id: 'fac-2',
    question: 'Emiten factura fiscal?',
    answer:
      'Si. Emitimos factura fiscal valida para Bolivia y otros paises de Latinoamerica. Al configurar tu cuenta puedes ingresar los datos fiscales de tu empresa (NIT/RUT, razon social) para que las facturas se generen automaticamente.',
    category: 'facturacion',
  },
  {
    id: 'fac-3',
    question: 'Cual es la politica de reembolso?',
    answer:
      'Ofrecemos reembolso completo dentro de los primeros 14 dias de contratacion si no estas satisfecho. Despues de ese periodo, puedes cancelar en cualquier momento y tendras acceso hasta el final del periodo ya facturado.',
    category: 'facturacion',
  },
  // Seguridad
  {
    id: 'seg-1',
    question: 'Donde se almacenan mis datos?',
    answer:
      'Los datos se almacenan en servidores seguros con encriptacion en reposo y en transito. La base de datos PostgreSQL tiene backups automaticos diarios con retencion de 30 dias. La infraestructura esta disenada para alta disponibilidad.',
    category: 'seguridad',
  },
  {
    id: 'seg-2',
    question: 'Como protegen la informacion de precios de mi empresa?',
    answer:
      'La arquitectura multi-tenant asegura aislamiento completo entre empresas. Cada tenant tiene sus datos en particiones separadas. El acceso se controla mediante autenticacion JWT, control de acceso basado en roles (RBAC) y todas las comunicaciones estan cifradas via HTTPS/TLS.',
    category: 'seguridad',
  },
  {
    id: 'seg-3',
    question: 'Tienen certificaciones de seguridad?',
    answer:
      'La plataforma implementa mejores practicas de seguridad incluyendo: encriptacion de contrasenas con bcrypt, tokens JWT con expiracion automatica, validacion de datos con Zod en todas las entradas, y proteccion contra ataques comunes (XSS, CSRF, SQL Injection via ORM).',
    category: 'seguridad',
  },
  {
    id: 'seg-4',
    question: 'Que pasa con mis datos si cancelo la suscripcion?',
    answer:
      'Al cancelar, tus datos permanecen accesibles en modo de solo lectura durante 30 dias. Puedes exportar toda tu informacion durante ese periodo. Despues de 30 dias, los datos se eliminan permanentemente de nuestros servidores segun nuestra politica de retencion.',
    category: 'seguridad',
  },
];

// Combine original FAQ items with extra items
const allFaqItems: ExtendedFaqItem[] = [
  ...faqItems.map((item) => ({ ...item, category: item.category as string })),
  ...extraFaqItems,
];

const ALL_CATEGORIES = [
  ...FAQ_CATEGORIES.map((c) => ({ value: c.value as string, label: c.label })),
  ...EXTRA_CATEGORIES,
];

export function FaqPage() {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const filteredItems = useMemo(() => {
    let items = allFaqItems;

    if (activeCategory !== 'all') {
      items = items.filter((item) => item.category === activeCategory);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      items = items.filter(
        (item) =>
          item.question.toLowerCase().includes(query) || item.answer.toLowerCase().includes(query)
      );
    }

    return items;
  }, [searchQuery, activeCategory]);

  function toggleItem(id: string) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  function getCategoryLabel(category: string): string {
    return ALL_CATEGORIES.find((c) => c.value === category)?.label ?? category;
  }

  return (
    <DocsLayout
      title="Preguntas Frecuentes"
      description="Encuentra respuestas a las preguntas mas comunes sobre Prixia."
    >
      <div className="space-y-6">
        {/* Search bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Buscar en preguntas frecuentes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Category filter chips */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setActiveCategory('all')}
            className={cn(
              'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
              activeCategory === 'all'
                ? 'border-blue-500 bg-blue-500 text-white'
                : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            )}
          >
            Todas ({allFaqItems.length})
          </button>
          {ALL_CATEGORIES.map((cat) => {
            const count = allFaqItems.filter((i) => i.category === cat.value).length;
            if (count === 0) return null;
            return (
              <button
                key={cat.value}
                onClick={() => setActiveCategory(cat.value)}
                className={cn(
                  'inline-flex items-center rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  activeCategory === cat.value
                    ? 'border-blue-500 bg-blue-500 text-white'
                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
                )}
              >
                {cat.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Result count */}
        <p className="text-sm text-gray-500 dark:text-gray-400">
          {filteredItems.length} {filteredItems.length === 1 ? 'resultado' : 'resultados'}
          {searchQuery.trim() && (
            <span>
              {' '}
              para &ldquo;
              <span className="font-medium text-gray-900 dark:text-white">{searchQuery}</span>
              &rdquo;
            </span>
          )}
        </p>

        {/* FAQ accordion */}
        <div className="space-y-2">
          {filteredItems.map((item) => {
            const isExpanded = expandedIds.has(item.id);
            return (
              <div
                key={item.id}
                className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950 hover:shadow-sm transition-shadow"
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className="flex w-full items-start justify-between gap-4 px-4 py-4 text-left"
                >
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    <span className="mt-0.5 shrink-0">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {getCategoryLabel(item.category)}
                      </Badge>
                    </span>
                    <span className="text-sm font-medium leading-snug text-gray-900 dark:text-white">
                      {item.question}
                    </span>
                  </div>
                  <ChevronDown
                    className={cn(
                      'mt-0.5 h-4 w-4 shrink-0 text-gray-400 transition-transform duration-200',
                      isExpanded && 'rotate-180'
                    )}
                  />
                </button>

                <div
                  className={cn(
                    'grid transition-all duration-200 ease-in-out',
                    isExpanded ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  )}
                >
                  <div className="overflow-hidden">
                    <div className="border-t border-gray-200 dark:border-gray-800 px-4 pb-4 pt-3">
                      <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-400">
                        {item.answer}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Empty state */}
        {filteredItems.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Search className="mb-3 h-10 w-10 text-gray-300 dark:text-gray-600" />
            <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
              No se encontraron resultados
            </p>
            <p className="mt-1 text-xs text-gray-400 dark:text-gray-500">
              Intenta con otros terminos de busqueda o cambia la categoria
            </p>
          </div>
        )}
      </div>
    </DocsLayout>
  );
}
